import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import Papa from "papaparse";
import Swal from "sweetalert2";

import { driveClient } from "./googleDrive";
import { createSyncEngine } from "./syncEngine";
import { syncStore as store } from "./syncState";
import { createAppStore } from "./appStore";
import { rootKey, forgetTypedSpellings } from "./searchHistory";

import {
  signInWithGoogle,
  initGapiClient,
  signOutGoogle,
  getAccessToken,
  isSignInRequired,
  OtherAccountError,
} from "../utils/googleAuth";

import { trackGoogleSignIn, trackSettingChanged } from "./analytics";
import { saveTextFile } from "./saveFile";
import { addToastSwipeToDismiss } from "./utils";
import posthog from "posthog-js";
import { Capacitor } from "@capacitor/core";

const AppContext = createContext();
const SyncStatusContext = createContext("idle");

const POLL_INTERVAL_MS = 30_000;
const DEBOUNCE_MS = 2000;

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  showCloseButton: true,
  didOpen: () => addToastSwipeToDismiss(),
});

// PostHog gets a one-way, app-specific hash of the Google account id ("sub"), never the
// id itself, as the privacy policy promises. The same on the web and in the apps, so
// one account stays one person there.
const analyticsIdFor = async (sub) => {
  if (!sub || !window.crypto?.subtle) return null;
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(`hanswehr:${sub}`));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
};

// One engine for the app session, wired to the real Drive adapter + local store.
const engine = createSyncEngine({ drive: driveClient, store });

// Dev-only sync tracing. Enable in devtools: localStorage.setItem("hw-sync-debug","1").
// No-op (and never throws) otherwise — safe to leave in production.
const dbg = (...a) => {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("hw-sync-debug")) {
      console.log("[hw-sync]", new Date().toISOString(), ...a);
    }
  } catch {
    /* ignore */
  }
};

export const AppContextProvider = ({ children }) => {
  // ── State ─────────────────────────────────────────────────────────────────────
  const [bookmarks, setBookmarks] = useState(
    JSON.parse(localStorage.getItem("bookmarks")) || {}
  );
  const [flashcards, setFlashcards] = useState(
    JSON.parse(localStorage.getItem("flashcards")) || {}
  );
  const HISTORY_KEY = "search_history";
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY)) || [];
    } catch {
      return [];
    }
  });

  // Live refs so debounced/polled syncs always read the LATEST state, never a
  // stale snapshot captured at schedule time (prevents clobbering a poll merge).
  const bookmarksRef = useRef(bookmarks);
  bookmarksRef.current = bookmarks;
  const flashcardsRef = useRef(flashcards);
  flashcardsRef.current = flashcards;
  const historyRef = useRef(searchHistory);
  historyRef.current = searchHistory;

  const bookmarksSyncTimer = useRef(null);
  const flashcardsSyncTimer = useRef(null);
  const historySyncTimer = useRef(null);
  const pollingRef = useRef(false);

  // Framework-free canonical state + all mutation logic (shared verbatim with the
  // multi-device test client). React state below is a render MIRROR of this. Seeded
  // once from localStorage; created lazily so it survives re-renders.
  const appRef = useRef(null);
  if (!appRef.current) {
    appRef.current = createAppStore({
      store,
      now: Date.now,
      initial: { collections: flashcards, bookmarks, history: searchHistory },
    });
  }
  const app = appRef.current;

  // Each bookmark's addedAt (the Bookmarks page sorts by it), mirrored from the sync
  // store. A sync can change only these (a root re-bookmarked on another device), so
  // it's re-read on every bookmark commit AND every bookmark sync, not only when the
  // bookmarks themselves change.
  const [bookmarksMeta, setBookmarksMeta] = useState(() => store.getBookmarksMeta());
  const refreshBookmarksMeta = () =>
    setBookmarksMeta((prev) => {
      const next = store.getBookmarksMeta();
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });

  // Commit helpers: update the ref synchronously (so a debounced/polled sync reads
  // the latest state) AND schedule a render.
  const commitFlashcards = (next) => {
    flashcardsRef.current = next;
    setFlashcards(next);
  };
  const commitBookmarks = (next) => {
    bookmarksRef.current = next;
    setBookmarks(next);
    refreshBookmarksMeta();
  };
  const commitHistory = (next) => {
    historyRef.current = next;
    setSearchHistory(next);
  };

  // ── Settings ─────────────────────────────────────────────────────────────────
  const DEFAULT_SETTINGS = { highContrastDefs: true, fontScale: 1, analyticsOptOut: false, hideSignIn: false };
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_SETTINGS,
    ...JSON.parse(localStorage.getItem("hw-settings") || "{}"),
  }));
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem("hw-settings", JSON.stringify(next));
      return next;
    });
    trackSettingChanged({ key, value });
  }, []);
  useEffect(() => {
    if (settings.analyticsOptOut) posthog.opt_out_capturing();
    else posthog.opt_in_capturing();
  }, [settings.analyticsOptOut]);

  // ── Sync status (derived from an in-flight counter to avoid flicker) ──────────
  const [syncStatus, setSyncStatus] = useState(navigator.onLine ? "idle" : "offline");
  const inflightRef = useRef(0);
  const syncingTimerRef = useRef(null);
  // Only surface "syncing" if a sync is actually slow (>350ms). Fast background
  // syncs (the common case) never flip the indicator, so it stops flickering.
  const SYNCING_INDICATOR_DELAY = 350;
  const beginSync = () => {
    inflightRef.current += 1;
    if (!syncingTimerRef.current) {
      dbg("indicator: show-scheduled (350ms)", "inflight=", inflightRef.current);
      syncingTimerRef.current = setTimeout(() => {
        syncingTimerRef.current = null;
        if (inflightRef.current > 0 && navigator.onLine) {
          dbg("indicator: SHOW (sync >350ms)");
          setSyncStatus("syncing");
        }
      }, SYNCING_INDICATOR_DELAY);
    }
  };
  const endSync = (hadError) => {
    inflightRef.current = Math.max(0, inflightRef.current - 1);
    if (hadError && isSignInRequired()) askToSignInAgain();
    if (inflightRef.current === 0) {
      if (syncingTimerRef.current) {
        clearTimeout(syncingTimerRef.current);
        syncingTimerRef.current = null;
      }
      // Offline wins: a sync that failed because the network went away isn't a sync
      // failure, and the 'online' event runs it again.
      const next = !navigator.onLine ? "offline" : hadError ? "error" : "idle";
      dbg("indicator: settle ->", next);
      setSyncStatus(next);
    }
  };

  // A native Google session this device can't renew (see googleAuth) only comes back
  // with a new sign-in, so ask for one, never over another dialog or toast, and only
  // until the user answers (Sign in, close or swipe). Another popup can replace it
  // unanswered (SweetAlert2 then never closes it), and a Sign in that couldn't happen
  // (offline, failed, another account; cancelling is an answer) doesn't count either:
  // it's asked again at the next sync.
  const askedToSignInRef = useRef(false);
  const askToSignInAgain = () => {
    if (askedToSignInRef.current || document.hidden || Swal.isVisible()) return;
    const email = JSON.parse(localStorage.getItem("userData") || "null")?.email;
    Toast.fire({
      icon: "info",
      titleText: "Sign in again to sync",
      text: email
        ? `Your Google sign-in for ${email} has expired on this device.`
        : "Your Google sign-in has expired on this device.",
      showConfirmButton: true,
      confirmButtonText: "Sign in",
      willClose: () => {
        askedToSignInRef.current = true;
      },
    }).then((result) => {
      if (result.isConfirmed) handleLogin();
    });
  };

  // Syncs wait, keeping their pending flags, while there's no token, while offline (the
  // 'online' event reconciles them) and until the user signs in again.
  const canSync = () => {
    if (!getAccessToken() || !navigator.onLine) return false;
    if (!isSignInRequired()) return true;
    setSyncStatus("error");
    askToSignInAgain();
    return false;
  };

  // ── Local-state snapshots for the engine ──────────────────────────────────────
  // Passed as a GETTER to the engine so the latest ref state is read inside the
  // engine's serialized section (never a stale snapshot).
  const buildBookmarkLocal = app.buildBookmarkLocal;
  const buildFlashcardLocal = app.buildFlashcardLocal;

  // ── Apply merged engine results back into React state (only when changed) ─────
  // Refs are updated SYNCHRONOUSLY so a follow-up sync reads the merged state,
  // never the pre-merge snapshot (renders flush asynchronously).
  // A change made while the sync was in flight is already folded into the engine's
  // result (B1), so app.applyX installs it as-is. We then mirror it into React.
  const applyBookmarks = (merged) => {
    const next = app.applyBookmarks(merged);
    if (JSON.stringify(next) !== JSON.stringify(bookmarksRef.current)) {
      localStorage.setItem("bookmarks", JSON.stringify(next));
      commitBookmarks(next);
    } else {
      refreshBookmarksMeta(); // same bookmarks, but their addedAt may have moved
    }
  };
  const applyHistory = (merged) => {
    const next = app.applyHistory(merged);
    if (JSON.stringify(next) !== JSON.stringify(historyRef.current)) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      commitHistory(next);
    }
  };
  const applyFlashcards = (merged) => {
    const next = app.applyFlashcards(merged);
    if (JSON.stringify(next) !== JSON.stringify(flashcardsRef.current)) {
      localStorage.setItem("flashcards", JSON.stringify(next));
      commitFlashcards(next);
    }
  };

  // ── Sync runners (read-merge-write-back via the engine) ───────────────────────
  const syncBookmarksNow = async () => {
    if (!canSync()) {
      store.setPendingSyncFlag("bookmarks", true);
      return;
    }
    beginSync();
    const t = Date.now();
    dbg("bookmarks sync: start");
    let err = false;
    try {
      applyBookmarks(await engine.syncBookmarks(buildBookmarkLocal));
    } catch (e) {
      console.error("Bookmark sync failed", e);
      store.setPendingSyncFlag("bookmarks", true);
      err = true;
    } finally {
      dbg(`bookmarks sync: ${err ? "ERROR" : "done"} (${Date.now() - t}ms)`);
      endSync(err);
    }
  };

  const syncHistoryNow = async () => {
    if (!canSync()) {
      store.setPendingSyncFlag("history", true);
      return;
    }
    beginSync();
    const t = Date.now();
    dbg("history sync: start");
    let err = false;
    try {
      applyHistory(await engine.syncHistory(app.buildHistoryLocal));
    } catch (e) {
      console.error("History sync failed", e);
      store.setPendingSyncFlag("history", true);
      err = true;
    } finally {
      dbg(`history sync: ${err ? "ERROR" : "done"} (${Date.now() - t}ms)`);
      endSync(err);
    }
  };

  const syncFlashcardsNow = async () => {
    if (!canSync()) {
      store.setPendingSyncFlag("flashcards", true);
      return;
    }
    beginSync();
    const t = Date.now();
    dbg("flashcards sync: start");
    let err = false;
    try {
      applyFlashcards(await engine.syncFlashcards(buildFlashcardLocal));
    } catch (e) {
      console.error("Flashcard sync failed", e);
      store.setPendingSyncFlag("flashcards", true);
      err = true;
    } finally {
      dbg(`flashcards sync: ${err ? "ERROR" : "done"} (${Date.now() - t}ms)`);
      endSync(err);
    }
  };

  // Debounced schedulers. They set the pending flag immediately so a change is
  // never lost if the app is closed/killed before the timer fires.
  const scheduleBookmarksSync = () => {
    dbg("debounced schedule: bookmarks");
    store.setPendingSyncFlag("bookmarks", true);
    clearTimeout(bookmarksSyncTimer.current);
    bookmarksSyncTimer.current = setTimeout(syncBookmarksNow, DEBOUNCE_MS);
  };
  const scheduleHistorySync = () => {
    dbg("debounced schedule: history");
    store.setPendingSyncFlag("history", true);
    clearTimeout(historySyncTimer.current);
    historySyncTimer.current = setTimeout(syncHistoryNow, DEBOUNCE_MS);
  };
  const scheduleFlashcardsSync = () => {
    dbg("debounced schedule: flashcards");
    store.setPendingSyncFlag("flashcards", true);
    clearTimeout(flashcardsSyncTimer.current);
    flashcardsSyncTimer.current = setTimeout(syncFlashcardsNow, DEBOUNCE_MS);
  };
  // Cancel a pending debounce and sync immediately (rename/delete/clear).
  const flushFlashcards = () => {
    clearTimeout(flashcardsSyncTimer.current);
    return syncFlashcardsNow();
  };

  // ── One-time migration: stamp existing items with timestamps (non-destructive) ─
  useEffect(() => {
    const storedBookmarks = JSON.parse(localStorage.getItem("bookmarks") || "{}");
    const meta = store.getBookmarksMeta();
    for (const word of Object.keys(storedBookmarks)) {
      if (!meta[word]) store.setBookmarkMeta(word, { addedAt: 1, lastModified: 1 });
    }
    refreshBookmarksMeta();

    const storedFc = JSON.parse(localStorage.getItem("flashcards") || "{}");
    const needsStamp = Object.values(storedFc)
      .flat()
      .some((c) => !c.addedAt && !c.lastModified);
    if (needsStamp) {
      const stamped = Object.fromEntries(
        Object.entries(storedFc).map(([name, cards]) => [
          name,
          cards.map((c) => ({
            ...c,
            addedAt: c.addedAt ?? 1,
            lastModified: c.lastModified ?? c.addedAt ?? 1,
          })),
        ])
      );
      commitFlashcards(app.setCollections(stamped)); // keep app (the source of truth) in sync
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persistence (debounced localStorage writes) ───────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem("bookmarks", JSON.stringify(bookmarks)), 500);
    return () => clearTimeout(t);
  }, [bookmarks]);
  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem("flashcards", JSON.stringify(flashcards)), 500);
    return () => clearTimeout(t);
  }, [flashcards]);

  // ── Bookmarks API ─────────────────────────────────────────────────────────────
  const addBookmark = useCallback((word, definitions) => {
    commitBookmarks(app.addBookmark(word, definitions));
    scheduleBookmarksSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeBookmark = useCallback((word) => {
    commitBookmarks(app.removeBookmark(word));
    scheduleBookmarksSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearBookmarks = () => {
    commitBookmarks(app.clearBookmarks());
    localStorage.removeItem("bookmarks");
    syncBookmarksNow();
  };

  // ── Search history API ────────────────────────────────────────────────────────
  const addToHistory = useCallback((word, rootData) => {
    // Stored under the root's search key, however the route spelled it (سأل, سءل and
    // سال are all سال), so one root keeps one entry here and on Drive. `root` is the
    // dictionary's own spelling (سأل), which the History card shows.
    const entry = { word: rootKey(word), timestamp: Date.now() };
    if (rootData?.root) entry.root = rootData.root;
    if (rootData) {
      const firstDef = rootData.definitions?.[0];
      const firstNoun = rootData.nouns?.[0];
      const firstEntry = firstDef || firstNoun;
      if (firstEntry) {
        entry.arabic = firstEntry.text || "";
        entry.transliteration = firstEntry.transliteration || "";
        const rawDef = firstEntry.translation?.short || firstEntry.translation?.text || "";
        entry.definition = rawDef.replace(/<[^>]*>/g, "");
        entry.forms = [...new Set((rootData.definitions || []).map((d) => d.form).filter(Boolean))];
      }
    }
    const next = app.addToHistory(entry);
    commitHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    scheduleHistorySync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeHistoryItem = useCallback((word) => {
    const next = app.removeHistoryItem(word); // records a tombstone so it stays deleted (B2)
    commitHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    scheduleHistorySync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearHistory = () => {
    const next = app.clearHistory(); // tombstones every entry so clear actually sticks (B2)
    commitHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    syncHistoryNow();
  };

  // ── Flashcards: version migration (non-destructive timestamp normalisation) ───
  const CURRENT_FLASHCARDS_VERSION = "1.1";
  useEffect(() => {
    const stored = localStorage.getItem("flashcards_version");
    if (stored !== CURRENT_FLASHCARDS_VERSION) {
      // Previously this WIPED flashcards on a version bump (data loss). Now it
      // only records the version; the timestamp migration above keeps old cards.
      localStorage.setItem("flashcards_version", CURRENT_FLASHCARDS_VERSION);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick Add's collection, followed by its id: a rename or delete, made here or
  // arriving through a sync, then can't leave it naming a collection that's gone,
  // which Quick Add would create again.
  const lastUsedRef = useRef(null);
  const setLastUsedCollection = (name) => {
    lastUsedRef.current = followCollection(name);
  };
  const lastUsedCollection = () => {
    const name = lastUsedRef.current?.();
    return name !== undefined && Object.prototype.hasOwnProperty.call(app.getCollections(), name) ? name : null;
  };

  // All flashcard mutations delegate to the shared appStore (same logic the
  // multi-device tests exercise) and mirror the result into React state.
  const addCollection = useCallback((collectionName = lastUsedCollection()) => {
    if (Object.prototype.hasOwnProperty.call(app.getCollections(), collectionName)) return;
    commitFlashcards(app.addCollection(collectionName));
    scheduleFlashcardsSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns whether the card was actually inserted (false if it was already there).
  const addCollectionWithFlashcard = (word, form, definition, root, wordID, collectionName) => {
    const before = app.getCollections();
    const next = app.addFlashcard(word, form, definition, root, wordID, collectionName);
    if (next !== before) {
      commitFlashcards(next);
      scheduleFlashcardsSync();
    }
    setLastUsedCollection(collectionName);
    return next !== before;
  };

  // Returns the collection's name afterwards: newName, or oldName if the store refused
  // (same name, or a collection called newName already exists, e.g. synced meanwhile).
  const renameCollection = (oldName, newName) => {
    const before = app.getCollections();
    if (!Object.prototype.hasOwnProperty.call(before, oldName)) return oldName;
    const next = app.renameCollection(oldName, newName);
    if (next === before) return oldName;
    commitFlashcards(next);
    // Quick Add's collection had no id to follow (data from before ids, signed out).
    if (lastUsedRef.current?.() === oldName) setLastUsedCollection(newName);
    flushFlashcards();
    return newName;
  };

  const deleteCollection = (collectionName) => {
    if (Object.prototype.hasOwnProperty.call(app.getCollections(), collectionName)) {
      const next = app.deleteCollection(collectionName);
      commitFlashcards(next);
      flushFlashcards();
      return Object.keys(next).length ? Object.keys(next)[0] : "";
    }
  };

  const getCollectionNames = () => Object.keys(flashcards);

  // Returns { collection, inserted }: inserted is false when the word was already in
  // that collection, so callers must not offer an Undo (it would delete the old card).
  const addFlashcard = (word, form, definition, root, wordID, selectedCollection = lastUsedCollection()) => {
    if (!selectedCollection) selectedCollection = getCollectionNames()[0];
    const before = app.getCollections();
    const next = app.addFlashcard(word, form, definition, root, wordID, selectedCollection);
    if (next !== before) {
      commitFlashcards(next);
      scheduleFlashcardsSync();
    }
    setLastUsedCollection(selectedCollection);
    return { collection: selectedCollection, inserted: next !== before };
  };

  const moveFlashcard = useCallback((wordID, fromCollection, toCollection) => {
    const before = app.getCollections();
    const next = app.moveFlashcard(wordID, fromCollection, toCollection);
    if (next !== before) {
      commitFlashcards(next);
      scheduleFlashcardsSync();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns { card, index } of the removed card (what restoreFlashcard needs for an
  // Undo), or null when there was nothing to remove.
  const removeFlashcard = (wordID, selectedCollection = lastUsedCollection()) => {
    const before = app.getCollections();
    const list = Object.prototype.hasOwnProperty.call(before, selectedCollection) ? before[selectedCollection] : [];
    const index = list.findIndex((f) => f.wordID === wordID);
    const next = app.removeFlashcard(wordID, selectedCollection);
    if (next === before) return null;
    commitFlashcards(next);
    scheduleFlashcardsSync();
    return { card: list[index], index };
  };

  // A collection keeps its id through renames, and a sync can rename one under an
  // open Undo toast. Returns a function that gives the collection's name at that
  // later time: the one its id has now, else `name` (the collection was deleted, or
  // merged into a same-named one from another device, which took over its cards).
  const followCollection = (name) => {
    const ids = store.getCollectionIdMap();
    const id = Object.prototype.hasOwnProperty.call(ids, name) ? ids[name] : null;
    return () => {
      const now = store.getCollectionIdMap();
      return (id && Object.keys(now).find((n) => now[n] === id)) || name;
    };
  };

  // Undo of a delete: puts the card back where it was, with its original addedAt.
  const restoreFlashcard = (card, collectionName, index) => {
    const before = app.getCollections();
    const next = app.restoreFlashcard(card, collectionName, index);
    if (next !== before) {
      commitFlashcards(next);
      scheduleFlashcardsSync();
    }
  };

  // Immutable, positional card edit that bumps lastModified (so edits propagate).
  const editFlashcard = (collectionName, index, patch) => {
    const before = app.getCollections();
    const next = app.editFlashcard(collectionName, index, patch);
    if (next !== before) {
      commitFlashcards(next);
      scheduleFlashcardsSync();
    }
  };

  // Back-compat: components call updateDBFlashcards() to request a sync.
  const updateDBFlashcards = () => scheduleFlashcardsSync();

  // ── Export / Import ───────────────────────────────────────────────────────────
  const showExportError = (err) =>
    Swal.fire({ icon: "error", title: "Export failed", text: err?.message || String(err) });

  const handleExportCSV = (selectedCollection) => {
    const csvData = flashcards[selectedCollection].map((flashcard) => ({
      word: flashcard.word,
      definition: flashcard.definition,
    }));

    Swal.fire({
      title: "Enter filename for CSV export",
      input: "text",
      inputValue: `${selectedCollection}_flashcards.csv`,
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return "Filename is required";
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      const name = /\.csv$/i.test(result.value) ? result.value : `${result.value}.csv`;
      // Leading BOM so Excel opens the Arabic text as UTF-8.
      saveTextFile(name, "\uFEFF" + Papa.unparse(csvData), "text/csv;charset=utf-8;").catch(showExportError);
    });
  };

  const handleExportJSON = () => {
    const timestamp = new Date().toLocaleString().replace(/,|:|\//g, "_");
    return saveTextFile(`flashcards_${timestamp}.json`, JSON.stringify(flashcards), "application/json").catch(showExportError);
  };

  const handleImportJSON = (event) => {
    const fileInput = event.target;
    if (fileInput.files && fileInput.files.length > 0) {
      const file = fileInput.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          const imported = jsonData.flashcards || (typeof jsonData === "object" ? jsonData : null);
          if (imported) {
            // Import is additive vs Drive: the engine no longer trusts a lingering
            // id-map entry, so collections NOT in the file are pulled back from Drive
            // rather than silently emptied (B3).
            commitFlashcards(app.importJSON(imported));
            scheduleFlashcardsSync();
          } else {
            console.error('Invalid JSON format: Missing "flashcards" key');
          }
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      };
      reader.readAsText(file);
      fileInput.value = "";
    }
  };

  // ── Auth ────────────────────────────────────────────────────────────────────
  const [userData, setUserData] = useState(JSON.parse(localStorage.getItem("userData")) || null);

  // Unconditional full push+pull of all three types (parallel — independent files).
  // Used once on app start and on sign-in, so local-only data is guaranteed pushed.
  const syncAll = (source = "syncAll") => {
    dbg(`trigger: ${source} -> full syncAll`);
    return Promise.all([syncHistoryNow(), syncBookmarksNow(), syncFlashcardsNow()]);
  };

  // Lightweight sync: only touch types that actually changed remotely OR have a
  // pending local write. Used by the 30s poll, on foreground, and on reconnect —
  // so the "syncing" indicator only appears when there's genuinely work to do.
  const syncChanged = async (source = "poll") => {
    if (document.hidden || !canSync()) {
      dbg(`trigger: ${source} -> skipped (hidden / no token / offline / sign-in needed)`);
      return;
    }
    if (pollingRef.current) {
      dbg(`trigger: ${source} -> skipped (already in flight)`);
      return; // re-entrancy guard (shared with the poll)
    }
    pollingRef.current = true;
    try {
      const changes = await engine.detectRemoteChanges();
      // The check swallows Drive errors, so this is where a dead session can first show.
      if (!canSync()) return;
      const pending = store.getPendingSyncFlags();
      const jobs = [];
      if (changes.history || pending.history) jobs.push(syncHistoryNow());
      if (changes.bookmarks || pending.bookmarks) jobs.push(syncBookmarksNow());
      if (changes.flashcards || pending.flashcards) jobs.push(syncFlashcardsNow());
      dbg(`trigger: ${source}`, "changed=", changes, "pending=", pending, "-> syncing", jobs.length, "type(s)");
      await Promise.all(jobs);
    } catch (err) {
      console.warn("[sync] change check failed:", err);
    } finally {
      pollingRef.current = false;
    }
  };

  const handleLogin = async () => {
    // Google's sign-in page can't load offline: the app would open a "No internet" page.
    if (!navigator.onLine) {
      askedToSignInRef.current = false;
      Toast.fire({ icon: "info", titleText: "You're offline", text: "Connect to the internet to sign in.", timer: 4000 });
      return;
    }
    try {
      await initGapiClient();
      const userInfo = await signInWithGoogle();
      if (!userInfo?.accessToken) throw new Error("Access token not received");

      askedToSignInRef.current = false;
      setUserData(userInfo);
      localStorage.setItem("userData", JSON.stringify(userInfo));
      const analyticsId = await analyticsIdFor(userInfo.id).catch(() => null);
      if (analyticsId) posthog.identify(analyticsId, { platform: Capacitor.getPlatform() });
      trackGoogleSignIn({ platform: Capacitor.getPlatform() });

      // The [userData] effect runs the initial full read-merge-write once userData
      // lands (pulls remote AND pushes any local-only pre-sign-in data) — so we
      // don't call syncAll() here to avoid a duplicate cycle.
    } catch (err) {
      console.error("Google Login Failed", err);
      const msg = err?.message || "";
      const cancelled = msg.toLowerCase().includes("cancel");
      if (!cancelled) askedToSignInRef.current = false;
      if (err instanceof OtherAccountError) {
        Swal.fire({
          icon: "warning",
          titleText: err.email ? `Sign in as ${err.email}` : "Sign in with the same account",
          text: "The flashcards, bookmarks and history on this device sync with that Google account. To switch accounts, sign out first.",
        });
      } else if (msg && !cancelled) {
        Swal.fire({ icon: "error", title: "Sign in failed", text: msg });
      }
    }
  };

  const handleLogout = async () => {
    // B5: flush pending changes to Drive BEFORE wiping local state + tombstones, and
    // cancel debounce timers so a queued sync can't run against the wiped state.
    // Otherwise offline/queued deletions are lost and resurrect on the next sign-in
    // (their tombstones are destroyed while Drive still holds the deleted items).
    clearTimeout(bookmarksSyncTimer.current);
    clearTimeout(flashcardsSyncTimer.current);
    clearTimeout(historySyncTimer.current);
    if (getAccessToken() && navigator.onLine && !isSignInRequired()) {
      const flags = store.getPendingSyncFlags();
      try {
        await Promise.all([
          flags.history ? syncHistoryNow() : null,
          flags.bookmarks ? syncBookmarksNow() : null,
          flags.flashcards ? syncFlashcardsNow() : null,
        ]);
      } catch (e) {
        // Best-effort: if the flush fails we still log out (the user asked to), but
        // whatever didn't reach Drive stays only in local state we're about to clear.
        console.warn("[sync] logout flush failed", e);
      }
    }

    signOutGoogle();
    posthog.reset();
    setUserData(null);
    setSyncStatus("idle");
    inflightRef.current = 0;
    localStorage.removeItem("userData");
    localStorage.removeItem("bookmarks");
    localStorage.removeItem("flashcards");
    localStorage.removeItem("hw-flashcards-collection"); // the Flashcards page's open collection
    localStorage.removeItem(HISTORY_KEY);
    forgetTypedSpellings(); // the typed spellings kept for History's routes go with it
    sessionStorage.removeItem("accessToken");
    app.reset(); // wipe canonical state + all sync metadata
    commitFlashcards({});
    commitBookmarks({});
    commitHistory([]);
  };

  // ── Initial sync on mount + 30s poll for cross-device changes ─────────────────
  useEffect(() => {
    if (!userData) return;

    // Immediate full sync on mount (pull remote + push anything local/pending).
    if (getAccessToken()) syncAll("mount");

    // Thereafter only sync when something actually changed (keeps the indicator quiet).
    const intervalId = setInterval(() => {
      dbg("poll tick (30s)");
      syncChanged("poll");
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lifecycle: flush on hide, resync on foreground, online/offline ────────────
  useEffect(() => {
    // Force the latest in-memory state to localStorage NOW (the 500ms debounced
    // persistence timer is unreliable on mobile background/kill).
    const persistNow = () => {
      try {
        localStorage.setItem("bookmarks", JSON.stringify(bookmarksRef.current));
        localStorage.setItem("flashcards", JSON.stringify(flashcardsRef.current));
        localStorage.setItem(HISTORY_KEY, JSON.stringify(historyRef.current));
      } catch {
        /* quota — ignore */
      }
    };
    const flushPending = () => {
      persistNow();
      const flags = store.getPendingSyncFlags();
      if (flags.history || flags.bookmarks || flags.flashcards) {
        dbg("flushPending (hide/pagehide)", flags);
      }
      if (flags.history) syncHistoryNow();
      if (flags.bookmarks) syncBookmarksNow();
      if (flags.flashcards) syncFlashcardsNow();
    };

    const onVisibility = () => {
      if (document.hidden) {
        dbg("visibility: hidden -> flushPending");
        flushPending(); // best-effort persist + network flush before background/kill
      } else if (userData) {
        syncChanged("foreground"); // foreground → pull only what changed (no full-sync flash)
      }
    };
    const onOnline = () => {
      dbg("event: online");
      setSyncStatus((s) => (s === "offline" && inflightRef.current === 0 ? "idle" : s));
      if (userData) syncChanged("online"); // reconnect → reconcile only what changed/pending
    };
    const onOffline = () => {
      dbg("event: offline");
      setSyncStatus("offline");
    };
    const onPageHide = () => {
      dbg("event: pagehide -> flushPending");
      flushPending();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize context value so syncStatus changes don't re-render all consumers.
  const ctxValue = useMemo(
    () => ({
      bookmarks,
      bookmarksMeta,
      setBookmarks,
      addBookmark,
      removeBookmark,
      clearBookmarks,
      flashcards,
      setFlashcards,
      addCollection,
      removeFlashcard,
      restoreFlashcard,
      followCollection,
      moveFlashcard,
      addFlashcard,
      deleteCollection,
      renameCollection,
      getCollectionNames,
      handleExportCSV,
      addCollectionWithFlashcard,
      editFlashcard,
      userData,
      setUserData,
      handleLogin,
      handleLogout,
      updateDBFlashcards,
      handleExportJSON,
      handleImportJSON,
      searchHistory,
      addToHistory,
      removeHistoryItem,
      clearHistory,
      settings,
      updateSetting,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bookmarks,
      bookmarksMeta,
      flashcards,
      userData,
      searchHistory,
      addBookmark,
      removeBookmark,
      addToHistory,
      removeHistoryItem,
      addCollection,
      moveFlashcard,
      settings,
      updateSetting,
    ]
  );

  return (
    <SyncStatusContext.Provider value={syncStatus}>
      <AppContext.Provider value={ctxValue}>{children}</AppContext.Provider>
    </SyncStatusContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
export const useSyncStatus = () => useContext(SyncStatusContext);

export const renderGoogleLogin = (elementId, callback) => {
  window.google.accounts.id.initialize({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    callback: (response) => {
      const idToken = response.credential;
      const base64Url = idToken.split(".")[1];
      const userInfo = JSON.parse(atob(base64Url));
      callback(userInfo);
    },
  });

  window.google.accounts.id.renderButton(document.getElementById(elementId), {
    theme: "outline",
    size: "large",
  });
};
